import { AttendanceService } from './../../attendance/services/attendance.service';
import { AlertService } from 'src/app/shared/store/alert/alert.service';
import { MonitorFilter } from '../models/monitor-filter.model';
import { LocalizationService } from 'src/app/services/localization/localization.service';
import { Action, State, StateContext, Store } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { MonitorActions } from './monitor.actions';
import { patch, updateItem } from '@ngxs/store/operators';
import {
  CloseServiceAttendanceMonitorResponse,
  SchoolAttendanceMonitorResponse,
  ServiceConceptDayResponse,
} from '../models/school-attendance-monitor.model';
import {
  ServiceAttendanceMonitorResponse,
  ServiceStudentAttendanceMonitorResponse,
  StudentAttendanceMonitorResponse,
  StudentAttendanceValidateMonitorRequest,
  StudentsAttendancesValidateMonitorRequest,
} from '../models/student-attendance-monitor.model';
import { UpdateAttendanceDayRequest } from '../models/save-student-attendance-monitor.model';
import { StudentAttendanceMonitorItem } from '../models/student-attendance-monitor-item.mode';
import { SchoolAttendanceMonitorItem } from '../models/school-attendance-monitor-item.model';
import { SaveSchoolAttendanceMonitorRequest } from '../models/save-school-attendance-monitor.model';
import { CourseResponse } from '../../course/models/course.model';
import { StudentAttendancePredictionResponse } from '../models/student-attendance-prediction.model';
import { SaveStudentAttendancePredictionRequest } from '../models/save-student-attendance-prediction.model';
import { ServiceAttendanceAdjustmentItem } from '../models/service-attendance-adjustment-item.model';
import { ServiceAttendanceAdjustmentResponse } from '../models/service-attendance-adjustment-response.model';
import { ServiceAttendanceAdjustmentFilter } from '../models/service-attendance-adjustment-filter.model';
import { SaveServiceAttendanceAdjustmentRequest } from '../models/save-service-attendance-adjustment.model';

export class MonitorStateModel {
  filters: {
    model: MonitorFilter;
  };
  adjustmentFilters: {
    model: ServiceAttendanceAdjustmentFilter;
  };
  isLoadingMonitors: boolean;
  isContractForStudents: boolean;
  isListSmaller: boolean;
  reloadData: boolean;
  schoolAttendanceMonitor: SchoolAttendanceMonitorResponse;
  serviceStudentAttendanceMonitor: ServiceStudentAttendanceMonitorResponse;
  studentAttendanceMonitorItems: StudentAttendanceMonitorItem[];
  schoolAttendanceMonitorItems: SchoolAttendanceMonitorItem[];
  schoolAttendanceMonitorCourses: CourseResponse[];
  studentAttendancePredictionForm: {
    model: StudentAttendancePredictionResponse;
  };
  serviceAttendanceAdjustment: ServiceAttendanceAdjustmentResponse;
  serviceAttendanceAdjustmentItems: ServiceAttendanceAdjustmentItem[];
  public error: string;
}

const defaults = {
  filters: {
    model: null,
  },
  adjustmentFilters: {
    model: null,
  },
  isLoadingMonitors: false,
  isContractForStudents: false,
  isListSmaller: true,
  reloadData: false,
  schoolAttendanceMonitor: null,
  serviceStudentAttendanceMonitor: null,
  serviceAttendanceAdjustment: null,
  studentAttendanceMonitorItems: [],
  schoolAttendanceMonitorItems: [],
  schoolAttendanceMonitorCourses: [],
  studentAttendancePredictionForm: {
    model: null,
  },
  serviceAttendanceAdjustmentItems: null,
  error: null,
};

@State<MonitorStateModel>({
  name: 'monitor',
  defaults,
})
@Injectable()
export class MonitorState {
  constructor(
    public store: Store,
    private alertService: AlertService,
    private localizationService: LocalizationService,
    private attendanceService: AttendanceService
  ) {}

  @Action(MonitorActions.SetFilterCurrentExercise)
  setFilterCurrentExercise(
    ctx: StateContext<MonitorStateModel>,
    { exerciseId, startDate, endDate }: MonitorActions.SetFilterCurrentExercise
  ) {
    ctx.setState(
      patch({
        filters: patch({
          model: patch({
            exerciseId: exerciseId,
            exerciseStartDate: startDate,
            exerciseEndDate: endDate,
          }),
        }),
      })
    );
  }

  @Action(MonitorActions.SetFilterDate)
  setFilterDate(
    ctx: StateContext<MonitorStateModel>,
    { date }: MonitorActions.SetFilterDate
  ) {
    ctx.setState(
      patch({
        filters: patch({
          model: patch({
            filterDate: date,
          }),
        }),
      })
    );
  }

  @Action(MonitorActions.GetAttendances)
  getMonitorAttendances(
    ctx: StateContext<MonitorStateModel>,
    { isContractForStudents }: MonitorActions.GetAttendances
  ) {
    var state = ctx.getState();
    ctx.patchState({ isLoadingMonitors: true, reloadData: true });
    const filters = state.filters.model;
    if (isContractForStudents) {
      return this.attendanceService
        .getStudentAttendanceMonitor(filters)
        .subscribe({
          next: (response) => {
            ctx.dispatch(
              new MonitorActions.GetStudentAttendanceMonitorSuccess(
                response,
                filters.serviceId
              )
            );
          },
          error: (error) => ctx.dispatch(new MonitorActions.Error(error, '')),
        });
    } else {
      return this.attendanceService
        .getSchoolAttendanceMonitor(filters)
        .subscribe({
          next: (response) => {
            ctx.dispatch(
              new MonitorActions.GetSchoolAttendanceMonitorSuccess(response)
            );
          },
          error: (error) => ctx.dispatch(new MonitorActions.Error(error, '')),
        });
    }
  }

  @Action(MonitorActions.GetSchoolAttendanceMonitorSuccess)
  getSchoolAttendancesMonitorSuccess(
    ctx: StateContext<MonitorStateModel>,
    { response }: MonitorActions.GetSchoolAttendanceMonitorSuccess
  ) {
    let closeServiceAttendanceMonitor = response.closeServiceAttendanceMonitor;
    let serviceConceptDay = response.serviceConceptDay;

    ctx.setState(
      patch({
        isLoadingMonitors: false,
        schoolAttendanceMonitor: response,
        schoolAttendanceMonitorItems: this.createSchoolAttendanceMonitorItems(
          serviceConceptDay,
          closeServiceAttendanceMonitor
        ),
      })
    );
  }

  @Action(MonitorActions.GetStudentAttendanceMonitorSuccess)
  getStudentAttendancesMonitorSuccess(
    ctx: StateContext<MonitorStateModel>,
    { response, serviceId }: MonitorActions.GetStudentAttendanceMonitorSuccess
  ) {
    if (!response || !response.serviceAttendanceMonitor) {
      ctx.setState(
        patch({
          isLoadingMonitors: false,
          serviceStudentAttendanceMonitor: response,
          studentAttendanceMonitorItems: [],
          schoolAttendanceMonitorCourses: [],
        })
      );
      return;
    }

    let serviceAttendanceMonitor = response.serviceAttendanceMonitor.find(
      (x) => x.serviceId == serviceId
    );

    ctx.setState(
      patch({
        isLoadingMonitors: false,
        serviceStudentAttendanceMonitor: response,
        studentAttendanceMonitorItems: this.getStudentAttendanceMonitorItems(
          serviceAttendanceMonitor,
          serviceId
        ),
        schoolAttendanceMonitorCourses: this.getSchoolAttendanceMonitorCourses(
          serviceAttendanceMonitor
        ),
      })
    );
  }

  @Action(MonitorActions.GetStudentAttendancePrediction)
  getStudentAttendancePrediction(
    ctx: StateContext<MonitorStateModel>,
    {}: MonitorActions.GetStudentAttendancePrediction
  ) {
    var state = ctx.getState();
    ctx.patchState({ isLoadingMonitors: true });
    const filters = state.filters.model;
    return this.attendanceService
      .getStudentAttendancePrediction(filters)
      .subscribe({
        next: (response) =>
          ctx.dispatch(
            new MonitorActions.GetStudentAttendancePredictionSuccess(response)
          ),
        error: (error) => ctx.dispatch(new MonitorActions.Error(error, '')),
      });
  }

  @Action(MonitorActions.GetStudentAttendancePredictionSuccess)
  getStudentAttendancePredictionSuccess(
    ctx: StateContext<MonitorStateModel>,
    { response }: MonitorActions.GetStudentAttendancePredictionSuccess
  ) {
    ctx.setState(
      patch({
        isLoadingMonitors: false,
        studentAttendancePredictionForm: patch({ model: response }),
        studentAttendanceMonitorItems: [],
      })
    );
  }

  @Action(MonitorActions.GetServiceAttendanceAdjustment)
  getServiceAttendanceAdjustment(
    ctx: StateContext<MonitorStateModel>,
    {}: MonitorActions.GetServiceAttendanceAdjustment
  ) {
    var state = ctx.getState();
    ctx.patchState({ isLoadingMonitors: true, reloadData: true });
    //const filters = state.adjustmentFilters.model;
    const filters = state.filters.model;
    return this.attendanceService
      .getServiceAttendanceAdjustment(filters)
      .subscribe({
        next: (response) => {
          ctx.dispatch(
            new MonitorActions.GetServiceAttendanceAdjustmentSuccess(response)
          );
        },
        error: (error) => ctx.dispatch(new MonitorActions.Error(error, '')),
      });
  }

  @Action(MonitorActions.GetServiceAttendanceAdjustmentSuccess)
  getServiceAttendanceAdjustmentSuccess(
    ctx: StateContext<MonitorStateModel>,
    { response }: MonitorActions.GetServiceAttendanceAdjustmentSuccess
  ) {
    ctx.setState(
      patch({
        isLoadingMonitors: false,
        serviceAttendanceAdjustment: response,
        serviceAttendanceAdjustmentItems:
          this.createServiceAttendanceAdjustmentItems(response),
      })
    );
  }

  @Action(MonitorActions.EditAttendanceStatus)
  editAttendanceStatus(
    ctx: StateContext<MonitorStateModel>,
    { index, attendanceService }: MonitorActions.EditAttendanceStatus
  ) {
    var state = ctx.getState();
    let studentAttendanceMonitorItem = state.studentAttendanceMonitorItems.find(
      (x) => x.index === index
    );
    let serviceStudentAttendanceMonitor = state.serviceStudentAttendanceMonitor;
    let serviceAttendanceMonitor =
      serviceStudentAttendanceMonitor.serviceAttendanceMonitor.find(
        (x) => x.serviceId == studentAttendanceMonitorItem.serviceId
      );
    let studentAttendanceMonitor =
      serviceAttendanceMonitor.studentAttendanceMonitor.find(
        (x) => x.studentId == studentAttendanceMonitorItem.studentId
      );

    let isEdited =
      studentAttendanceMonitor.attendanceService !== attendanceService;
    ctx.setState(
      patch({
        studentAttendanceMonitorItems: updateItem<StudentAttendanceMonitorItem>(
          (x) => x.index === index,
          patch({
            attendanceService: attendanceService,
            attendanceState: attendanceService,
            edited: isEdited,
          })
        ),
      })
    );
  }

  @Action(MonitorActions.EditSchoolAttendanceAverage)
  editSchoolAttendanceAverage(
    ctx: StateContext<MonitorStateModel>,
    { index, average }: MonitorActions.EditSchoolAttendanceAverage
  ) {
    var state = ctx.getState();
    let schoolAttendanceMonitorItem = state.schoolAttendanceMonitorItems.find(
      (x) => x.index === index
    );
    let schoolAttendanceMonitor = state.schoolAttendanceMonitor;
    let serviceConcept =
      schoolAttendanceMonitor.serviceConceptDay.serviceConcepts.find(
        (x) =>
          x.serviceConceptId == schoolAttendanceMonitorItem.serviceConceptId
      );
    let isEdited = serviceConcept.average != average;
    ctx.setState(
      patch({
        schoolAttendanceMonitorItems: updateItem<SchoolAttendanceMonitorItem>(
          (x) => x.index === index,
          patch({
            average: average,
            edited: isEdited,
          })
        ),
      })
    );
  }

  @Action(MonitorActions.EditServiceAttendanceAdjustment)
  editServiceAttendanceAdjustment(
    ctx: StateContext<MonitorStateModel>,
    { index, adjustment }: MonitorActions.EditServiceAttendanceAdjustment
  ) {
    var state = ctx.getState();
    let serviceAttendanceAdjustmentItem =
      state.serviceAttendanceAdjustmentItems.find((x) => x.index === index);
    let serviceAttendanceAdjustment = state.serviceAttendanceAdjustment;
    let serviceConcept =
      serviceAttendanceAdjustment.serviceConceptAdjustments.find(
        (x) =>
          x.serviceConceptId == serviceAttendanceAdjustmentItem.serviceConceptId
      );
    let totalAttendance: number =
      serviceConcept.totalAttendance + Number(adjustment);
    let isEdited = serviceConcept.adjust != adjustment;
    ctx.setState(
      patch({
        serviceAttendanceAdjustmentItems:
          updateItem<ServiceAttendanceAdjustmentItem>(
            (x) => x.index === index,
            patch({
              total: totalAttendance,
              edited: isEdited,
              adjust: adjustment,
            })
          ),
      })
    );
  }

  @Action(MonitorActions.SaveStudentAttendanceStatus)
  saveStudentAttendanceStatus(
    ctx: StateContext<MonitorStateModel>,
    { index, user }: MonitorActions.SaveStudentAttendanceStatus
  ) {
    var state = ctx.getState();
    let studentAttendanceMonitorItem = state.studentAttendanceMonitorItems.find(
      (x) => x.index === index
    );
    let serviceStudentAttendanceMonitor = state.serviceStudentAttendanceMonitor;
    let serviceAttendanceMonitor =
      serviceStudentAttendanceMonitor.serviceAttendanceMonitor.find(
        (x) => x.serviceId == studentAttendanceMonitorItem.serviceId
      );
    let studentAttendanceMonitor =
      serviceAttendanceMonitor.studentAttendanceMonitor.find(
        (x) => x.studentId === studentAttendanceMonitorItem.studentId
      );

    var saveStudentAttendanceMonitorRequest =
      this.createUpdateAttendanceDayRequest(
        studentAttendanceMonitorItem,
        serviceStudentAttendanceMonitor
      );
    this.attendanceService
      .saveStudentAttendanceMonitorStatus(
        saveStudentAttendanceMonitorRequest,
        user
      )
      .subscribe({
        next: () => {
          ctx.dispatch(new MonitorActions.SaveStudentAttendanceStatusSuccess());
        },
        error: (error) => ctx.dispatch(new MonitorActions.Error(error, '')),
      });

    /*     
      this.alertService
      .confirm(
        this.localizationService.translate(
          'monitors.daily-attendances-students'
        ),
        this.localizationService.translate(
          'monitors.saveStudentAttendanceStatusConfirm'
        ),
        this.localizationService.translate('buttons.accept'),
        this.localizationService.translate('buttons.cancel'),
        this.localizationService
          .translate('monitors.saveStudentAttendanceStatusConfirm')
          .replace(
            '{0}',
            this.localizationService.translate(
              studentAttendanceMonitor.studentName +
                ' ' +
                studentAttendanceMonitor.studentSurname +
                ' ' +
                studentAttendanceMonitor.studentSecondSurname
            )
          )
          .replace(
            '{1}',
            this.localizationService.translate(
              AttendanceServiceEnum[
                studentAttendanceMonitorItem.attendanceService
              ].toString()
            )
          )
      )
      .then((result) => {
        if (result.value) {
          var saveStudentAttendanceMonitorRequest =
            this.createUpdateAttendanceDayRequest(
              studentAttendanceMonitorItem,
              serviceStudentAttendanceMonitor
            );
          this.attendanceService
            .saveStudentAttendanceMonitorStatus(
              saveStudentAttendanceMonitorRequest,
              user
            )
            .subscribe({
              next: () => {
                ctx.dispatch(
                  new MonitorActions.SaveStudentAttendanceStatusSuccess()
                );
              },
              error: (error) =>
                ctx.dispatch(new MonitorActions.Error(error, '')),
            });
        } else {
          return null;
        }
      }); 
      */
  }

  @Action(MonitorActions.SaveStudentAttendanceStatusSuccess)
  saveStudentAttendanceStatusSuccess(
    ctx: StateContext<MonitorStateModel>,
    {}: MonitorActions.SaveStudentAttendanceStatusSuccess
  ) {
    ctx.setState(
      patch({
        isLoadingMonitors: false,
      })
    );
  }

  @Action(MonitorActions.ValidateStudentAttendance)
  validateStudentAttendance(
    ctx: StateContext<MonitorStateModel>,
    { user }: MonitorActions.SaveStudentAttendanceStatus
  ) {
    var state = ctx.getState();
    let studentAttendanceMonitorItems = state.studentAttendanceMonitorItems;
    let serviceStudentAttendanceMonitor = state.serviceStudentAttendanceMonitor;

    let students: StudentAttendanceValidateMonitorRequest[] = [];
    studentAttendanceMonitorItems.forEach((element) => {
      let item = new StudentAttendanceValidateMonitorRequest();
      item.attendanceService = element.attendanceService;
      item.studentId = element.studentId;
      students.push(item);
    });

    this.alertService
      .confirm(
        this.localizationService.translate(
          'monitors.daily-attendances-students'
        ),
        this.localizationService.translate(
          'monitors.validateStudentAttendanceConfirm'
        ),
        this.localizationService.translate('buttons.accept'),
        this.localizationService.translate('buttons.cancel'),
        this.localizationService.translate(
          'monitors.validateStudentAttendanceConfirm'
        )
      )
      .then((result) => {
        if (result.value) {
          var validateStudentAttendanceMonitorRequest =
            this.createValidateAttendanceDayRequest(
              students,
              serviceStudentAttendanceMonitor
            );
          this.attendanceService
            .validateStudentAttendanceMonitorStatus(
              validateStudentAttendanceMonitorRequest,
              user
            )
            .subscribe({
              next: () => {
                ctx.dispatch(
                  new MonitorActions.ValidateStudentAttendanceSuccess()
                );
              },
              error: (error) =>
                ctx.dispatch(new MonitorActions.Error(error, '')),
            });
        } else {
          return null;
        }
      });
  }

  @Action(MonitorActions.ValidateStudentAttendanceSuccess)
  validateStudentAttendanceSuccess(
    ctx: StateContext<MonitorStateModel>,
    {}: MonitorActions.SaveStudentAttendanceStatusSuccess
  ) {
    ctx.setState(
      patch({
        isLoadingMonitors: false,
      })
    );
  }

  @Action(MonitorActions.SaveSchoolAttendance)
  saveSchoolAttendanceAverage(
    ctx: StateContext<MonitorStateModel>,
    { user }: MonitorActions.SaveSchoolAttendance
  ) {
    var state = ctx.getState();
    let schoolAttendanceMonitor = state.schoolAttendanceMonitor;
    let stateSchoolAttendanceMonitorItems = state.schoolAttendanceMonitorItems;

    ctx.patchState({ isLoadingMonitors: true });
    var saveSchoolAttendanceMonitorRequest =
      this.createSaveSchoolAttendanceMonitorRequest(
        schoolAttendanceMonitor,
        stateSchoolAttendanceMonitorItems
      );
    this.attendanceService
      .saveSchoolAttendanceMonitor(saveSchoolAttendanceMonitorRequest, user)
      .subscribe({
        next: () => {
          ctx.dispatch(new MonitorActions.SaveSchoolAttendanceSuccess());
        },
        error: (error) => ctx.dispatch(new MonitorActions.Error(error, '')),
      });
  }

  @Action(MonitorActions.SaveSchoolAttendanceSuccess)
  saveSchoolAttendanceSuccess(
    ctx: StateContext<MonitorStateModel>,
    {}: MonitorActions.SaveSchoolAttendanceSuccess
  ) {
    ctx.setState(
      patch({
        isLoadingMonitors: false,
        schoolAttendanceMonitorItems: ctx
          .getState()
          .schoolAttendanceMonitorItems.map((item) => {
            return { ...item, edited: false };
          }),
      })
    );
  }

  @Action(MonitorActions.SaveStudentAttendancePrediction)
  saveStudentAttendancePrediction(
    ctx: StateContext<MonitorStateModel>,
    {
      studentAttendancePredictionForm,
      user,
    }: MonitorActions.SaveStudentAttendancePrediction
  ) {
    this.alertService
      .confirm(
        this.localizationService.translate('monitors.title'),
        this.localizationService.translate(
          'monitors.saveStudentAttendancePredictionConfirm'
        ),
        this.localizationService.translate('buttons.accept'),
        this.localizationService.translate('buttons.cancel'),
        this.localizationService.translate(
          'monitors.saveStudentAttendancePredictionConfirm'
        )
      )
      .then((result) => {
        if (result.value) {
          ctx.patchState({ isLoadingMonitors: true });
          var saveStudentAttendancePredictionRequest =
            this.createSaveStudentAttendancePredictionRequest(
              studentAttendancePredictionForm,
              ctx
            );
          this.attendanceService
            .saveStudentAttendancePrediction(
              saveStudentAttendancePredictionRequest,
              user
            )
            .subscribe({
              next: () => {
                ctx.dispatch(
                  new MonitorActions.SaveStudentAttendancePredictionSuccess()
                );
              },
              error: (error) =>
                ctx.dispatch(new MonitorActions.Error(error, '')),
            });
        } else {
          return null;
        }
      });
  }

  @Action(MonitorActions.SaveStudentAttendancePredictionSuccess)
  saveStudentAttendancePredictionSuccess(
    ctx: StateContext<MonitorStateModel>,
    {}: MonitorActions.SaveStudentAttendancePredictionSuccess
  ) {
    ctx.setState(
      patch({
        isLoadingMonitors: false,
      })
    );
  }

  @Action(MonitorActions.SaveServiceAttendanceAdjustment)
  saveServiceAttendanceAdjustment(
    ctx: StateContext<MonitorStateModel>,
    { user, status }: MonitorActions.SaveServiceAttendanceAdjustment
  ) {
    var state = ctx.getState();
    let serviceAttendanceAdjustment = state.serviceAttendanceAdjustment;
    let serviceAttendanceAdjustmentItems =
      state.serviceAttendanceAdjustmentItems;

    ctx.patchState({ isLoadingMonitors: true });
    var saveServiceAttendanceAdjustmentRequest =
      this.createSaveServiceAttendanceAdjustmentRequest(
        serviceAttendanceAdjustment,
        serviceAttendanceAdjustmentItems,
        status
      );
    this.attendanceService
      .saveServiceAttendanceAdjustment(
        saveServiceAttendanceAdjustmentRequest,
        user
      )
      .subscribe({
        next: () => {
          ctx.dispatch(
            new MonitorActions.SaveServiceAttendanceAdjustmentSuccess()
          );
        },
        error: (error) => ctx.dispatch(new MonitorActions.Error(error, '')),
      });
  }

  @Action(MonitorActions.SaveServiceAttendanceAdjustmentSuccess)
  saveServiceAttendanceAdjustmentSuccess(
    ctx: StateContext<MonitorStateModel>,
    {}: MonitorActions.SaveServiceAttendanceAdjustmentSuccess
  ) {
    var state = ctx.getState();
    let serviceAttendanceAdjustment = state.serviceAttendanceAdjustment;
    let serviceAttendanceAdjustmentItems =
      state.serviceAttendanceAdjustmentItems;
    let serviceConceptAdjustments =
      state.serviceAttendanceAdjustment.serviceConceptAdjustments;
    let serviceAttendanceAdjustmentItems2 = ctx.setState(
      patch({
        isLoadingMonitors: false,
        serviceAttendanceAdjustmentItems: serviceAttendanceAdjustmentItems.map(
          (item) => {
            return { ...item, edited: false };
          }
        ),
        serviceAttendanceAdjustment: patch({
          status: serviceAttendanceAdjustment.status,
          serviceConceptAdjustments: serviceConceptAdjustments.map((item) => {
            let serviceAttendanceAdjustmentItem =
              serviceAttendanceAdjustmentItems.find(
                (x) => x.serviceConceptId == item.serviceConceptId
              );
            return { ...item, adjust: serviceAttendanceAdjustmentItem.adjust };
          }),
        }),
      })
    );
  }

  @Action(MonitorActions.SetIsContractForStudents)
  setIsContractForStudents(
    ctx: StateContext<MonitorStateModel>,
    { isContractForStudents }: MonitorActions.SetIsContractForStudents
  ) {
    ctx.setState(
      patch({
        isContractForStudents: isContractForStudents,
      })
    );
  }

  @Action(MonitorActions.Error)
  onErrorOcurred(
    ctx: StateContext<MonitorStateModel>,
    { error, details }: MonitorActions.Error
  ) {
    ctx.patchState({
      error: error,
      isLoadingMonitors: false,
    });
    /* MappingUtilsService.showError(
      this.getTitle(),
      error,
      this.alertService,
      this.localizationService
    ); */
  }

  @Action(MonitorActions.SetIsListSmaller)
  setIsListSmaller(
    ctx: StateContext<MonitorStateModel>,
    { isListSmaller }: MonitorActions.SetIsListSmaller
  ) {
    ctx.patchState({
      isListSmaller: isListSmaller,
    });
  }

  @Action(MonitorActions.SetReloadData)
  setReloadData(
    ctx: StateContext<MonitorStateModel>,
    { reloadData }: MonitorActions.SetReloadData
  ) {
    ctx.patchState({
      reloadData,
    });
  }

  @Action(MonitorActions.SetDefaultState)
  setDefaultState(
    ctx: StateContext<MonitorStateModel>,
    {}: MonitorActions.SetDefaultState
  ) {
    ctx.patchState(defaults);
  }

  @Action(MonitorActions.SetStudentAttendanceMonitorDefaultState)
  setStudentAttendanceMonitorDefaultState(
    ctx: StateContext<MonitorStateModel>,
    {}: MonitorActions.SetStudentAttendanceMonitorDefaultState
  ) {
    ctx.setState(
      patch({
        studentAttendanceMonitorItems: defaults.studentAttendanceMonitorItems,
      })
    );
  }

  @Action(MonitorActions.SetSchoolAttendanceMonitorDefaultState)
  setSchoolAttendanceMonitorDefaultState(
    ctx: StateContext<MonitorStateModel>,
    {}: MonitorActions.SetSchoolAttendanceMonitorDefaultState
  ) {
    ctx.setState(
      patch({
        schoolAttendanceMonitorItems: defaults.schoolAttendanceMonitorItems,
      })
    );
  }

  @Action(MonitorActions.SetStudentAttendancePredictionFormDefaultState)
  setStudentAttendancePredictionFormDefaultState(
    ctx: StateContext<MonitorStateModel>,
    {}: MonitorActions.SetStudentAttendancePredictionFormDefaultState
  ) {
    ctx.setState(
      patch({
        studentAttendancePredictionForm:
          defaults.studentAttendancePredictionForm.model,
      })
    );
  }

  @Action(MonitorActions.SetServiceAttendanceAdjustmentDefaultState)
  setServiceAttendanceAdjustmentDefaultState(
    ctx: StateContext<MonitorStateModel>,
    {}: MonitorActions.SetServiceAttendanceAdjustmentDefaultState
  ) {
    ctx.setState(
      patch({
        serviceAttendanceAdjustmentItems:
          defaults.serviceAttendanceAdjustmentItems,
      })
    );
  }

  private createUpdateAttendanceDayRequest(
    studentAttendanceMonitorItem: StudentAttendanceMonitorItem,
    serviceStudentAttendanceMonitor: ServiceStudentAttendanceMonitorResponse
  ) {
    let updateAttendanceDayRequest = new UpdateAttendanceDayRequest();
    updateAttendanceDayRequest.exerciseId =
      serviceStudentAttendanceMonitor.exerciseId;
    updateAttendanceDayRequest.year = serviceStudentAttendanceMonitor.year;
    updateAttendanceDayRequest.month = serviceStudentAttendanceMonitor.month;
    updateAttendanceDayRequest.day = serviceStudentAttendanceMonitor.day;
    updateAttendanceDayRequest.schoolId =
      serviceStudentAttendanceMonitor.schoolId;
    updateAttendanceDayRequest.serviceId =
      studentAttendanceMonitorItem.serviceId;
    updateAttendanceDayRequest.studentId =
      studentAttendanceMonitorItem.studentId;
    updateAttendanceDayRequest.attendanceState =
      studentAttendanceMonitorItem.attendanceState;
    return updateAttendanceDayRequest;
  }

  private createValidateAttendanceDayRequest(
    studentAttendanceMonitor: StudentAttendanceValidateMonitorRequest[],
    serviceStudentAttendanceMonitorResponse: ServiceStudentAttendanceMonitorResponse
  ) {
    let studentsAttendancesValidateMonitorRequest =
      new StudentsAttendancesValidateMonitorRequest();
    //studentsAttendancesValidateMonitorRequest.exerciseId = serviceStudentAttendanceMonitorResponse.exerciseId;
    studentsAttendancesValidateMonitorRequest.year =
      serviceStudentAttendanceMonitorResponse.year;
    studentsAttendancesValidateMonitorRequest.month =
      serviceStudentAttendanceMonitorResponse.month;
    studentsAttendancesValidateMonitorRequest.day =
      serviceStudentAttendanceMonitorResponse.day;
    studentsAttendancesValidateMonitorRequest.schoolId =
      serviceStudentAttendanceMonitorResponse.schoolId;
    studentsAttendancesValidateMonitorRequest.serviceId =
      serviceStudentAttendanceMonitorResponse.serviceAttendanceMonitor[0]?.serviceId;
      studentsAttendancesValidateMonitorRequest.isHoliday =
      serviceStudentAttendanceMonitorResponse.isHoliday;
    studentsAttendancesValidateMonitorRequest.attendanceStudent =
      studentAttendanceMonitor;
    return studentsAttendancesValidateMonitorRequest;
  }

  private createStudentAttendanceMonitorItem(
    index: number,
    serviceId: number,
    item: StudentAttendanceMonitorResponse,
    serviceAttendanceMonitor: ServiceAttendanceMonitorResponse
  ) {
    let studentAttendanceMonitorItem = new StudentAttendanceMonitorItem();
    studentAttendanceMonitorItem.index = index;
    studentAttendanceMonitorItem.serviceId = serviceId;
    studentAttendanceMonitorItem.studentId = item.studentId;
    (studentAttendanceMonitorItem.studentName =
      item.studentName +
      ' ' +
      item.studentSurname +
      ' ' +
      item.studentSecondSurname),
      (studentAttendanceMonitorItem.attendanceState = item.attendanceState),
      (studentAttendanceMonitorItem.attendanceService = item.attendanceService),
      (studentAttendanceMonitorItem.schoolarshipName = item.schoolarshipName),
      (studentAttendanceMonitorItem.edited = false);
    studentAttendanceMonitorItem.serviceName =
      serviceAttendanceMonitor.serviceName;
    studentAttendanceMonitorItem.schoolarCourse = item.schoolarCourse;
    studentAttendanceMonitorItem.schoolarCourseId = item.schoolarCourseId;
    return studentAttendanceMonitorItem;
  }

  private createSaveSchoolAttendanceMonitorRequest(
    schoolAttendanceMonitor: SchoolAttendanceMonitorResponse,
    stateSchoolAttendanceMonitorItems: SchoolAttendanceMonitorItem[]
  ) {
    let saveSchoolAttendanceMonitorRequest =
      new SaveSchoolAttendanceMonitorRequest();
    saveSchoolAttendanceMonitorRequest.closingServiceId =
      schoolAttendanceMonitor.closeServiceAttendanceMonitor.closingServiceId;
    saveSchoolAttendanceMonitorRequest.schoolId =
      schoolAttendanceMonitor.serviceConceptDay.schoolId;
    saveSchoolAttendanceMonitorRequest.year =
      schoolAttendanceMonitor.serviceConceptDay.year;
    saveSchoolAttendanceMonitorRequest.month =
      schoolAttendanceMonitor.serviceConceptDay.month;
    saveSchoolAttendanceMonitorRequest.day =
      schoolAttendanceMonitor.serviceConceptDay.day;
    saveSchoolAttendanceMonitorRequest.serviceId =
      schoolAttendanceMonitor.serviceConceptDay.serviceId;
    saveSchoolAttendanceMonitorRequest.exerciseId =
      schoolAttendanceMonitor.serviceConceptDay.exerciseId;
    saveSchoolAttendanceMonitorRequest.serviceConceptDays =
      stateSchoolAttendanceMonitorItems.map((item) => {
        return {
          serviceConceptId: item.serviceConceptId,
          amount: item.average,
        };
      });
    return saveSchoolAttendanceMonitorRequest;
  }

  private getStudentAttendanceMonitorItems(
    serviceAttendanceMonitor: ServiceAttendanceMonitorResponse,
    serviceId: number
  ) {
    let studentAttendanceMonitorItems =
      serviceAttendanceMonitor.studentAttendanceMonitor.map((item, index) => {
        return this.createStudentAttendanceMonitorItem(
          index,
          serviceId,
          item,
          serviceAttendanceMonitor
        );
      });
    studentAttendanceMonitorItems = studentAttendanceMonitorItems.sort((a, b) =>
      a.studentName.localeCompare(b.studentName)
    );
    return studentAttendanceMonitorItems;
  }

  private getSchoolAttendanceMonitorCourses(
    serviceAttendanceMonitor: ServiceAttendanceMonitorResponse
  ) {
    let schoolAttendanceMonitorCourses =
      serviceAttendanceMonitor.studentAttendanceMonitor.map((item) => {
        let course = new CourseResponse();
        course.courseDescription = item.schoolarCourse;
        course.courseId = item.schoolarCourseId;
        return course;
      });
    schoolAttendanceMonitorCourses = schoolAttendanceMonitorCourses.filter(
      (item, i, arr) => arr.findIndex((t) => t.courseId === item.courseId) === i
    );
    schoolAttendanceMonitorCourses = schoolAttendanceMonitorCourses.sort(
      (a, b) => a.courseDescription.localeCompare(b.courseDescription)
    );
    return schoolAttendanceMonitorCourses;
  }

  private createSchoolAttendanceMonitorItems(
    serviceConceptDay: ServiceConceptDayResponse,
    closeServiceAttendanceMonitor: CloseServiceAttendanceMonitorResponse
  ) {
    let schoolAttendanceMonitorItems: SchoolAttendanceMonitorItem[] = [];
    serviceConceptDay.serviceConcepts?.map((item, index) => {
      let schoolAttendanceMonitorItem = new SchoolAttendanceMonitorItem();
      schoolAttendanceMonitorItem.status = closeServiceAttendanceMonitor.status;
      schoolAttendanceMonitorItem.serviceConceptId = item.serviceConceptId;
      schoolAttendanceMonitorItem.serviceName = item.serviceName;
      schoolAttendanceMonitorItem.average = item.average;
      schoolAttendanceMonitorItem.index = index;
      schoolAttendanceMonitorItem.edited = false;
      schoolAttendanceMonitorItems.push(schoolAttendanceMonitorItem);
    });
    return schoolAttendanceMonitorItems;
  }

  private createServiceAttendanceAdjustmentItems(
    response: ServiceAttendanceAdjustmentResponse
  ): ServiceAttendanceAdjustmentItem[] {
    return response.serviceConceptAdjustments.map((item, index) => {
      let serviceAttendanceAdjustmentItem =
        new ServiceAttendanceAdjustmentItem();
      serviceAttendanceAdjustmentItem.index = index;
      serviceAttendanceAdjustmentItem.serviceConceptId = item.serviceConceptId;
      serviceAttendanceAdjustmentItem.serviceConceptTypeName =
        item.serviceConceptTypeName;
      serviceAttendanceAdjustmentItem.actual = item.totalAttendance;
      serviceAttendanceAdjustmentItem.adjust = item.adjust;
      serviceAttendanceAdjustmentItem.total =
        item.totalAttendance + item.adjust;
      serviceAttendanceAdjustmentItem.edited = false;
      return serviceAttendanceAdjustmentItem;
    });
  }

  private createSaveServiceAttendanceAdjustmentRequest(
    serviceAttendanceAdjustment: ServiceAttendanceAdjustmentResponse,
    serviceAttendanceAdjustmentItems: ServiceAttendanceAdjustmentItem[],
    status: number
  ) {
    let saveServiceAttendanceAdjustmentRequest =
      new SaveServiceAttendanceAdjustmentRequest();
    saveServiceAttendanceAdjustmentRequest.closeAttendanceServiceId =
      serviceAttendanceAdjustment.closeAttendanceServiceId;
    saveServiceAttendanceAdjustmentRequest.exerciseId =
      serviceAttendanceAdjustment.exerciseId;
    saveServiceAttendanceAdjustmentRequest.serviceId =
      serviceAttendanceAdjustment.serviceId;
    saveServiceAttendanceAdjustmentRequest.schoolId =
      serviceAttendanceAdjustment.schoolId;
    saveServiceAttendanceAdjustmentRequest.month =
      serviceAttendanceAdjustment.month;
    saveServiceAttendanceAdjustmentRequest.year =
      serviceAttendanceAdjustment.year;
    saveServiceAttendanceAdjustmentRequest.status = status;
    saveServiceAttendanceAdjustmentRequest.concepServiceAdjustments =
      serviceAttendanceAdjustmentItems.map((item) => {
        return {
          conceptServiceId: item.serviceConceptId,
          adjust: item.adjust,
        };
      });

    return saveServiceAttendanceAdjustmentRequest;
  }

  private createSaveStudentAttendancePredictionRequest(
    studentAttendancePredictionForm: StudentAttendancePredictionResponse,
    ctx: StateContext<MonitorStateModel>
  ): SaveStudentAttendancePredictionRequest {
    var state = ctx.getState();
    let filter = state.filters;
    let saveStudentAttendancePredictionRequest =
      new SaveStudentAttendancePredictionRequest();
    saveStudentAttendancePredictionRequest.schoolId = filter.model.schoolId;
    saveStudentAttendancePredictionRequest.exerciseId = filter.model.exerciseId;
    saveStudentAttendancePredictionRequest.predictionDate =
      filter.model.filterDate.toString();
    saveStudentAttendancePredictionRequest.serviceId = filter.model.serviceId;
    saveStudentAttendancePredictionRequest.primarySchool =
      studentAttendancePredictionForm.primarySchool;
    saveStudentAttendancePredictionRequest.middleSchool =
      studentAttendancePredictionForm.middleSchool;
    saveStudentAttendancePredictionRequest.highSchool =
      studentAttendancePredictionForm.highSchool;
    return saveStudentAttendancePredictionRequest;
  }
}
